import React, { useCallback } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  Controller,
  FieldError,
  useFieldArray,
  useForm,
  UseFormSetValue,
} from 'react-hook-form'
import { InferType } from 'yup'
import { captureException } from '@sentry/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { societyPollCreateSchema } from '../../../../forms/schemas/society_poll_create'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { ToastType } from '../../../common/Toast/toast-type'
import { societyCreatePostSchema } from '../../../../forms/schemas/society_create_post'
import { FormControl } from '../../../common/FormControl'
import { TextInput } from '../../../common/TextInput'
import { useFormErrorMessage } from '../../../../hooks/useFormErrorMessage'
import { SelectDropdown } from '../../../common/SelectDropdown'
import { Button, ButtonVariant } from '../../../common/Button'
import { theme } from '../../../../theme/theme'
import { IconChoices } from '../../../common/Icon'
import { createPoll, patchPoll, pollQueries } from '../../../../api/polls'
import { Label } from '../../../common/Label'
import { Toggle } from '../../../common/Toggle'
import {
  CreatePollBody,
  PatchPollBody,
} from '../../../../generated/api/poll-commands'
import { PostPollCard } from '../../../../generated/api/post-row'
import { invalidatePostRows } from '../../postRowCache'

interface PollEditorProps {
  pollId?: string
  setPostValue: UseFormSetValue<InferType<typeof societyCreatePostSchema>>
  setDisplayPoll: React.Dispatch<React.SetStateAction<boolean>>
  watchSociety: string
  watchPollIds: (string | undefined)[] | undefined
}

export const PollEditor = ({
  pollId,
  setPostValue,
  setDisplayPoll,
  watchSociety,
  watchPollIds,
}: PollEditorProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { getErrorMessage } = useFormErrorMessage()
  const { setToastNotification } = useToastNotifications()

  const { data: poll } = useQuery(pollQueries.card(pollId))

  const updateMode = pollId !== undefined

  const showErrorMessage = (): void => {
    const message = updateMode
      ? 'pollEditor.flashMessage.updatePollFailure'
      : 'pollEditor.flashMessage.createPollFailure'
    setToastNotification(ToastType.DANGER, translate(message))
  }

  const getDefaultValues = useCallback((): InferType<
    typeof societyPollCreateSchema
  > => {
    return {
      title: updateMode ? poll?.title || '' : '',
      options:
        updateMode && poll
          ? poll.options.map((option) => ({
              id: option.id,
              text: option.text,
            }))
          : [{ text: '' }, { text: '' }],
      societyId: updateMode ? poll?.societyId || watchSociety : watchSociety,
      type: updateMode ? poll?.type || 'per-user' : 'per-user',
      anonymous: poll?.anonymous ?? true,
    }
  }, [poll, watchSociety, updateMode])

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({
    mode: 'all',
    resolver: yupResolver(societyPollCreateSchema),
    defaultValues: getDefaultValues(),
  })

  const { fields, append, remove } = useFieldArray({
    name: 'options',
    control,
  })

  const queryClient = useQueryClient()

  const onSaved = (card: PostPollCard): void => {
    if (!watchPollIds?.includes(card.id)) {
      setPostValue('pollIds', [...(watchPollIds || []), card.id], {
        shouldValidate: true,
        shouldDirty: true,
      })
    }
    queryClient.setQueryData(pollQueries.card(card.id).queryKey, card)
    invalidatePostRows(queryClient)
    setDisplayPoll(false)
  }

  const { mutate: mutatePatchPoll, isPending: isPatchingPoll } = useMutation({
    mutationFn: async (variables: { id: string; body: PatchPollBody }) =>
      (await patchPoll(variables)).data.data,
    meta: { skipGlobalInvalidate: true },
    onSuccess: onSaved,
    onError: () => {
      showErrorMessage()
    },
  })

  const { mutate: mutateCreatePoll, isPending: isCreatingPoll } = useMutation({
    mutationFn: async (body: CreatePollBody) =>
      (await createPoll(body)).data.data,
    meta: { skipGlobalInvalidate: true },
    onSuccess: onSaved,
    onError: () => {
      showErrorMessage()
    },
  })

  const onSubmit = async (
    data: InferType<typeof societyPollCreateSchema>
  ): Promise<void> => {
    const options = data.options.map((option) => ({
      ...(option.id ? { id: option.id } : {}),
      text: option.text,
    })) as CreatePollBody['options']

    if (updateMode && poll) {
      mutatePatchPoll({
        id: poll.id,
        body: {
          title: data.title,
          options,
          type: data.type,
          anonymous: data.anonymous,
        },
      })
    }
    if (!updateMode) {
      mutateCreatePoll({
        societyId: data.societyId,
        title: data.title,
        options,
        type: data.type,
        anonymous: data.anonymous,
      })
    }
  }

  const typeOptions = [
    {
      label: translate('pollEditor.label.perUser'),
      value: 'per-user',
    },
    {
      label: translate('pollEditor.label.perHousehold'),
      value: 'per-unit',
    },
  ]

  const onError = (error: unknown): void => {
    captureException(error)
    showErrorMessage()
  }

  const onClose = (): void => {
    setDisplayPoll(false)
  }

  const addAlternative = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ): void => {
    e.preventDefault()
    append({ text: '' }, { shouldFocus: true })
  }

  const loading = isCreatingPoll || isPatchingPoll

  return (
    <div className="flex flex-col gap-4 rounded border border-neutral-80 p-6">
      <div className="flex gap-8">
        <Controller
          control={control}
          name="title"
          render={({ field: { value, name, onChange, onBlur } }) => (
            <FormControl
              className="flex flex-1"
              label={translate('pollEditor.form.labels.title')}
              error={errors.title && getErrorMessage(errors.title)}
              name={name}
            >
              <TextInput
                placeholder="Aa"
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                error={!!errors.title}
              />
            </FormControl>
          )}
        />
        <Controller
          control={control}
          name="type"
          render={({ field: { onChange, value, name } }) => (
            <FormControl
              className="flex flex-1"
              label={translate('pollEditor.form.labels.type')}
              name={name}
              error={errors.type && getErrorMessage(errors.type)}
            >
              <SelectDropdown
                options={typeOptions}
                value={typeOptions.find((option) => option.value === value)}
                onChange={(option) => onChange(option?.value)}
                error={!!errors.type}
                height={49} // TODO: Align with textinput
              />
            </FormControl>
          )}
        />
      </div>
      <div>
        <Controller
          control={control}
          name="anonymous"
          render={({ field: { value, onChange, name } }) => (
            <div
              className="flex h-full w-full flex-col items-start justify-between
                          space-y-3 md:w-1/2 md:flex-row md:items-center md:space-x-10 md:space-y-0"
            >
              <Label
                name="anonymousVoting"
                label={translate('poll.anonymousVoting')}
                style={theme.textVariants.baseBold}
                className="whitespace-nowrap"
              />
              <Toggle
                name={name}
                enabled={value}
                disabled={!!poll}
                onChange={onChange}
              />
            </div>
          )}
        />
      </div>
      <div>
        <p className="mb-1" style={theme.textVariants.caption}>
          {translate('pollEditor.form.headers.options')}
        </p>
        <div className="flex flex-col gap-4">
          {fields.map((field, index) => (
            <Controller
              control={control}
              key={field.id}
              name={`options.${index}.text`}
              render={({ field: { name, onChange, onBlur, value } }) => (
                <FormControl
                  name={name}
                  error={
                    errors.options?.[index]?.text &&
                    getErrorMessage(errors.options?.[index]?.text as FieldError)
                  }
                >
                  <TextInput
                    placeholder={`${translate(
                      'pollEditor.form.placeholders.option'
                    )} ${index + 1}`}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    onDeleteIconPress={
                      index > 1 ? () => remove(index) : undefined
                    }
                  />
                </FormControl>
              )}
            />
          ))}
        </div>
      </div>
      <Button
        className="flex w-fit"
        onClick={addAlternative}
        icon={IconChoices.PLUS_SIGN}
        label={translate('pollEditor.form.buttons.addMoreOptionsButtonTitle')}
      />
      <div className="mt-4 flex flex-shrink-0 flex-wrap items-center justify-end gap-3">
        <Button
          label={translate('common.actions.cancel')}
          onClick={onClose}
          variant={ButtonVariant.TEXT}
        />
        <Button
          variant={ButtonVariant.PRIMARY}
          loading={loading}
          disabled={!isValid || loading}
          label={translate('common.actions.save')}
          onClick={handleSubmit(onSubmit, onError)}
        />
      </div>
    </div>
  )
}
